import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, ArrowRight } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { Button } from '../../components/ui/button';
import ServiceTestimonials from '../../components/ServiceTestimonials';
import ContactFormDialog from '../../components/ContactFormDialog';

const TrademarkSearch = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const pricingPlans = [
    { name: "Basic Search", price: "$45", features: ["USPTO Database Search", "Basic Conflict Report", "Email Delivery", "Email Support", "24-Hour Turnaround"] },
    { name: "Comprehensive Search", price: "$149", popular: true, features: ["Everything in Basic", "Common Law Search", "State Database Search", "Detailed Analysis Report", "Attorney Review", "Same-Day Results"] },
    { name: "Premium Search", price: "$299", features: ["Everything in Comprehensive", "International Search", "Industry-Specific Analysis", "Dedicated Attorney", "Risk Assessment", "Filing Recommendation"] }
  ];

  const process = [
    { step: "01", title: "Submit Your Mark", description: "Provide your proposed trademark and we'll begin a thorough search of USPTO and related databases." },
    { step: "02", title: "Comprehensive Analysis", description: "Our experts conduct detailed searches across federal, state, and common law databases for potential conflicts." },
    { step: "03", title: "Receive Detailed Report", description: "Get a comprehensive report with findings, risk assessment, and professional filing recommendations." }
  ];

  const testimonials = [
    { name: "Chris Taylor", role: "Startup Founder", text: "Thorough trademark search saved me from potential legal issues. Excellent service!" },
    { name: "Nina Patel", role: "Brand Manager", text: "Comprehensive search report helped me make informed decisions. Highly professional!" },
    { name: "Kevin Brown", role: "Product Developer", text: "Fast, detailed, and accurate trademark search. Great value for money!" },
    { name: "Laura Martinez", role: "Business Owner", text: "Expert analysis and clear recommendations. Very helpful team!" },
    { name: "Ryan Johnson", role: "Entrepreneur", text: "Professional trademark search service. Results delivered quickly!" }
  ];

  const faqs = [
    { question: "Why do I need a trademark search?", answer: "A trademark search identifies potential conflicts before filing, helping you avoid costly rejections, legal disputes, and rebranding expenses." },
    { question: "What databases are searched?", answer: "We search USPTO federal trademark database, state trademark registrations, common law sources, business directories, and domain registrations." },
    { question: "How long does a trademark search take?", answer: "Basic searches are completed within 24 hours, while comprehensive searches typically take 2-3 business days." },
    { question: "What if my search finds conflicts?", answer: "Our report includes alternative suggestions and professional guidance on how to proceed with your trademark application." },
    { question: "Is a trademark search required?", answer: "While not legally required, a trademark search is highly recommended to avoid application rejection and potential infringement issues." }
  ];

  return (
    <div className="min-h-screen bg-[#0F1419] text-white">
      <Header />
      <div className="pt-20 pb-16 bg-gradient-to-b from-[#131921] to-[#0F1419]">
        <div className="container mx-auto px-6 text-center">
          <Link to="/" className="inline-flex items-center text-[#FF9900] hover:text-orange-400 mb-6 transition-colors"><ArrowLeft className="w-4 h-4 mr-2"/>Back to Home</Link>
          <h1 className="text-5xl font-bold mb-6 text-[#FF9900]">Trademark Search Services</h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">Comprehensive trademark searches to protect your brand before filing. Starting at $45.</p>
        </div>
      </div>
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative h-96 rounded-3xl overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800">
              <img src="https://images.unsplash.com/photo-1674027001838-09e049c47212?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxkYXRhJTIwYW5hbHlzaXMlMjByZXNlYXJjaCUyMGxhcHRvcCUyMGlsbHVzdHJhdGlvbiUyMHZlY3RvcnxlbnwwfHx8fDE3NzUwNzI1MTF8MA&ixlib=rb-4.1.0&q=85" alt="Search" className="w-full h-full object-cover"/>
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6">About Trademark Search</h2>
              <p className="text-gray-300 mb-4 leading-relaxed">A comprehensive trademark search identifies potential conflicts before you invest in filing your application. Our expert team searches federal, state, and common law sources to ensure your mark is available.</p>
              <p className="text-gray-300 mb-6 leading-relaxed">Avoid costly rejections and legal disputes with our professional trademark search services, including detailed analysis and filing recommendations.</p>
              <Button 
                onClick={() => setIsContactFormOpen(true)}
                className="bg-[#FF9900] hover:bg-orange-600 text-white"
              >Get Started Today</Button>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Our Simple 3-Step Process</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {process.map((item, i) => (
              <div key={i} className="bg-[#0F1419] p-8 rounded-2xl border border-white/10 hover:border-[#FF9900]/50 transition-all">
                <div className="text-6xl font-bold text-[#FF9900] mb-4 opacity-20">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-gray-300 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-4">Trademark Search Packages</h2>
          <p className="text-center text-gray-300 mb-16">Choose the search level that meets your needs</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`relative bg-[#131921] p-8 rounded-2xl border-2 ${plan.popular ? 'border-[#FF9900]' : 'border-white/10'} hover:border-[#FF9900]/50 transition-all`}>
                {plan.popular && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2"><span className="bg-[#FF9900] text-white px-4 py-1 rounded-full text-sm font-bold">MOST POPULAR</span></div>}
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="text-5xl font-bold text-[#FF9900] mb-6">{plan.price}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f, idx) => (
                    <li key={idx} className="flex items-start"><Check className="w-5 h-5 text-[#FF9900] mr-2 mt-0.5 flex-shrink-0"/><span className="text-gray-300">{f}</span></li>
                  ))}
                </ul>
                <Button 
                  onClick={() => setIsContactFormOpen(true)}
                  className={`w-full ${plan.popular ? 'bg-[#FF9900] hover:bg-orange-600' : 'bg-white/10 hover:bg-white/20'} text-white`}>Choose {plan.name}</Button>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Why Choose Our Search Services?</h2>
              <div className="space-y-4">
                {["Comprehensive Database Coverage", "Expert Legal Analysis", "Fast Turnaround Time", "Detailed Reporting"].map((title, i) => (
                  <div key={i} className="flex items-start">
                    <Check className="w-6 h-6 text-[#FF9900] mr-3 mt-1 flex-shrink-0"/>
                    <div><h3 className="text-xl font-bold mb-2">{title}</h3><p className="text-gray-300">Professional trademark search with actionable insights</p></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1674027001860-f9e3a94f4084?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxzZWFyY2glMjBkYXRhYmFzZSUyMHJlc2VhcmNoJTIwYW5hbHlzaXMlMjB2ZWN0b3IlMjBpbGx1c3RyYXRpb258ZW58MHx8fHwxNzc1MDcyNDk2fDA&ixlib=rb-4.1.0&q=85" alt="Why Choose" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-r from-[#FF9900] to-orange-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Ready to Search Your Trademark?</h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">Get peace of mind with our comprehensive trademark search before filing your application.</p>
          <Button 
            onClick={() => setIsContactFormOpen(true)}
            className="bg-white text-[#FF9900] hover:bg-gray-100 text-lg px-8 py-6">Start Your Trademark Search<ArrowRight className="ml-2 w-5 h-5"/></Button>
        </div>
      </section>
      <ServiceTestimonials 
        testimonials={testimonials}
        serviceName="Trademark Search"
      />
      <section className="py-20 bg-[#131921]">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-16">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-[#0F1419] p-6 rounded-xl border border-white/10">
                <h3 className="text-xl font-bold mb-3 text-[#FF9900]">{faq.question}</h3>
                <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      
      <ContactFormDialog 
        isOpen={isContactFormOpen}
        onClose={() => setIsContactFormOpen(false)}
      />
    </div>
  );
};

export default TrademarkSearch;
